import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Checkbox } from 'ui-components/components/checkbox';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuItemGroup } from 'ui-components/components/dropdown-menu-item-group';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { Text } from 'ui-components/components/text/text';
import { TextType } from 'ui-components/components/text/text-type';

import { useVisiblePackingSlips } from '../../../../../hooks/use-visible-packing-slips';
import Global from '../../../../../store/global-selectors';
import { messages } from './messages';
import style from './select-store-dropdown.module.less';

type Store = {
  storeGuid: string;
  name: string;
  isActive: boolean;
  packingSlip?: {
    packingSlipId: string;
    htmlTemplateKey?: string;
  };
};

type SelectStoreDropdownProps = {
  packingSlipId?: string;
  onSelectionChange: (storeGuids: string[]) => void;
};

export const SelectStoreDropdown = ({ packingSlipId, onSelectionChange }: SelectStoreDropdownProps) => {
  const allStores = useSelector(Global.selectSellerActiveStores) as Store[];
  const packingSlips = useVisiblePackingSlips();

  const initialSelectedStores = useMemo(() => {
    if (!packingSlipId) {
      return [];
    }
    return allStores.filter((store) => store.packingSlip?.packingSlipId === packingSlipId).map((store) => store.storeGuid);
  }, [packingSlipId, allStores]);

  const [selectedStoreGuids, setSelectedStoreGuids] = useState<string[]>(initialSelectedStores);

  const packingSlipNameById = useMemo(
    () => Object.fromEntries(packingSlips.map((ps) => [String(ps.packingSlipId), ps.name])),
    [packingSlips],
  );

  const getAssignedTemplateName = (store: Store): string | null => {
    const { packingSlip } = store;
    if (!packingSlip?.packingSlipId) {
      return null;
    }

    return packingSlipNameById[String(packingSlip.packingSlipId)] ?? null;
  };

  const handleStoreToggle = useCallback(
    (storeGuid: string) => {
      const isSelected = selectedStoreGuids.includes(storeGuid);
      const newSelection = isSelected ? selectedStoreGuids.filter((guid) => guid !== storeGuid) : [...selectedStoreGuids, storeGuid];
      setSelectedStoreGuids(newSelection);
      onSelectionChange(newSelection);
    },
    [selectedStoreGuids, onSelectionChange],
  );

  const hasSelection = selectedStoreGuids.length > 0;

  const handleToggleAll = useCallback(() => {
    const newSelection = hasSelection ? [] : allStores.map((store) => store.storeGuid);
    setSelectedStoreGuids(newSelection);
    onSelectionChange(newSelection);
  }, [hasSelection, allStores, onSelectionChange]);

  return (
    <>
      <Text textType={TextType.Label} value={messages.storeSelectionLabel} />
      <DropdownMenu
        closeWhenChildrenClicked={false}
        disabled={allStores.length === 0}
        dropdownMenuShouldHaveWidthOfButton
        fullWidth
        variant='form'
      >
        <DropdownMenuToggler>{<FormattedMessage {...messages.storeSelectionPlaceholder} />}</DropdownMenuToggler>
        <DropdownMenuItemGroup label={<FormattedMessage {...messages.storesGroupLabel} />}>
          {allStores.map((store) => {
            const assignedTemplateName = getAssignedTemplateName(store);

            return (
              <DropdownMenuItem
                key={store.storeGuid}
                className={style['store-dropdown-item']}
                onClick={() => handleStoreToggle(store.storeGuid)}
              >
                <Checkbox
                  checked={selectedStoreGuids.includes(store.storeGuid)}
                  className={style['store-checkbox']}
                  innerClassname={style['store-checkbox-label']}
                  name={store.name}
                  onChange={() => handleStoreToggle(store.storeGuid)}
                >
                  <Text textType={TextType.Paragraph} value={store.name} />
                  {assignedTemplateName ? (
                    <Text className={style['assigned-template-label']} textType={TextType.Caption} value={assignedTemplateName} />
                  ) : null}
                </Checkbox>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuItemGroup>
        <DropdownMenuItem className={style['clear-item']} onClick={handleToggleAll}>
          <Text
            className={style['clear-button']}
            textType={TextType.Label}
            value={hasSelection ? messages.clearButton : messages.selectAllButton}
          />
        </DropdownMenuItem>
      </DropdownMenu>
    </>
  );
};
